/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Backdrop, Button, CircularProgress, Container, Grid, IconButton, makeStyles } from '@material-ui/core';
import { Close } from '@material-ui/icons';

import { deepCopy } from '../../utils/lodashUtils';
import useBlocRefHook from '../../utils/useBlocRef.hook';
import TableFooter from '../common/TableFooter';
import ResourcesHeader from '../resources/ResourcesHeader';
import ResourcesList from '../resources/ResourcesList';

const useStyle = makeStyles((theme) => {
  return {
    addButton: {
      paddingLeft: '16px',
      paddingRight: '16px',
    },
    backdrop: {
      color: '#fff',
      height: '100%',
      left: 0,
      position: 'absolute',
      top: 0,
      width: '100%',
      zIndex: theme.zIndex.drawer + 1,
    },
    closeButton: {
      position: 'absolute',
      right: '12px',
      top: '12px',
      zIndex: 1,
    },
    container: {
      backgroundColor: '#FFFFFF',
      padding: '24px',
      position: 'relative',
    },
    footerContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'end',
      marginTop: '12px',
    },
    headerContainer: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '16px',
    },
    title: {
      fontSize: '20px',
      fontWeight: 500,
    },
  };
});

export default ({ preSelectedMaterials, defaultCriteria, onSelectMaterials, onCloseViewer, isTechQA, videosOnly = false }) => {
  const blocRef = useBlocRefHook('resourcesBloc');
  const classes = useStyle();

  const [materials, setMaterials] = useState([]);
  const [criteria, setCriteria] = useState(blocRef.current.criteria$.getValue());
  const [materialCount, setMaterialCount] = useState(0);
  const [loadingResourceInfo, setLoadingResourceInfo] = useState();
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleCriteriaChange = useCallback(
    (c) => {
      blocRef.current.updateCriteria(c);
    },
    [blocRef]
  );

  const handleDeleteMaterial = useCallback(
    (material) => {
      blocRef.current.openViewer(material, { paper: classes.imagePaper }, { onClose: onCloseViewer });
    },
    [blocRef, onCloseViewer]
  );

  const handleRefresh = useCallback(() => {
    blocRef.current.refreshCriteria();
  }, [blocRef]);

  const handleViewMaterial = useCallback(
    (material) => {
      blocRef.current.openViewer(material, { paper: classes.imagePaper }, { onClose: !isTechQA ? () => onCloseViewer({ selectedMaterials }) : () => {} });
    },
    [onCloseViewer, selectedMaterials]
  );

  const handleEditMaterial = useCallback((material) => blocRef.current.openEditResource(material), []);
  const handleSelectMaterial = useCallback((material) => {
    setSelectedMaterials((prev) => {
      const cloneMaterials = deepCopy(prev);

      if (cloneMaterials.find((m) => m.id === material.id)) {
        return cloneMaterials.filter((m) => m.id !== material.id);
      }

      cloneMaterials.push(material);
      return cloneMaterials;
    });
  }, []);

  const handleAddClick = () => {
    onSelectMaterials(selectedMaterials);
  };

  const selectedMaterialIds = useMemo(() => {
    return (selectedMaterials || []).map((m) => m.id);
  }, [selectedMaterials]);

  useEffect(() => {
    const initialCriteria = defaultCriteria || {};
    const criteriaWithFilter = videosOnly ? { ...initialCriteria, type: 'video' } : initialCriteria;

    blocRef.current.updateCriteria(criteriaWithFilter);

    const materialsSub = blocRef.current.materials$.subscribe(setMaterials);
    const criteriaSub = blocRef.current.criteria$.subscribe(setCriteria);
    const countSub = blocRef.current.materialCount$.subscribe(setMaterialCount);
    const resourceUpdateSub = blocRef.current.listenToResourceUpdate().subscribe();
    const loadingResourceInfoSub = blocRef.current.loadingResourceInfo$.subscribe(setLoadingResourceInfo);
    const loadingSub = blocRef.current.materialsLoading$.subscribe(setLoading);

    return () => {
      materialsSub.unsubscribe();
      criteriaSub.unsubscribe();
      countSub.unsubscribe();
      resourceUpdateSub.unsubscribe();
      loadingResourceInfoSub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, [blocRef]);

  useEffect(() => {
    if (preSelectedMaterials?.length) {
      setSelectedMaterials(preSelectedMaterials);
    }
  }, [preSelectedMaterials]);

  return (
    <div className={classes.container}>
      <IconButton className={classes.closeButton} onClick={onCloseViewer} aria-label="close">
        <Close />
      </IconButton>

      <Grid container component={Container} style={{ marginTop: '24px' }}>
        <Grid item xs={12}>
          <ResourcesHeader
            disableType={!isTechQA}
            disableCategory
            disableTemplateView
            disableAddResource
            shouldHideSelfHelpMaterials
            onRefresh={handleRefresh}
            onCriteriaChange={handleCriteriaChange}
          />
        </Grid>

        <Grid item xs={12} className="mt-1">
          <div style={{ position: 'relative' }}>
            <Backdrop open={loading} className={classes.backdrop}>
              <CircularProgress color="inherit" />
            </Backdrop>

            <ResourcesList
              disableEditButton
              disableDeleteButton
              onViewMaterial={handleViewMaterial}
              onDeleteMaterial={handleDeleteMaterial}
              onEditMaterial={handleEditMaterial}
              materials={materials}
              onSelectMaterial={handleSelectMaterial}
              selectedMaterialIds={selectedMaterialIds}
              loadingResourceInfo={loadingResourceInfo}
              isSelectable
              isTechQA={isTechQA}
            />
          </div>
        </Grid>

        <Grid item xs={12}>
          <div style={{ textAlign: 'center' }}>
            <TableFooter count={materialCount} page={criteria.page} rowsPerPage={criteria.limit} changePage={(page) => handleCriteriaChange({ page })} />
          </div>
        </Grid>

        <Grid item xs={12}>
          <div className={classes.footerContainer}>
            <Button color="primary" variant="contained" disabled={!selectedMaterials?.length} onClick={handleAddClick} className={classes.footerContainer}>
              Add
            </Button>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};
